import { ChevronRight, Home } from "lucide-react";
import { motion } from "motion/react";
import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router";
import { useBackdrop } from "@/components/layout/backdrop";
import { Photo, type PhotoName } from "./photo";

export interface Crumb {
  label: string;
  to?: string;
}

/** A short vermilion brush stroke drawn under a title (tapers like a real stroke). */
export function BrushStroke({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 120 12" className={className} aria-hidden preserveAspectRatio="none">
      <path d="M2 8 C 22 3, 52 4, 78 6 S 112 7, 118 4" fill="none" stroke="var(--color-accent)" strokeWidth="4.5" strokeLinecap="round" className="animate-brush" style={{ ["--brush-length" as string]: 130 }} />
      <path d="M8 9.5 C 30 7, 60 8, 96 8.5" fill="none" stroke="var(--color-accent)" strokeOpacity="0.35" strokeWidth="1.4" strokeLinecap="round" className="animate-brush" style={{ ["--brush-length" as string]: 100 }} />
    </svg>
  );
}

type PageMood = "daily" | "editorial" | "research" | "analytics" | "setup" | "admin" | "security" | "empty";

const MOODS: Record<PageMood, { accent: string; glow: string; artPosition: string }> = {
  daily: { accent: "#1f466f", glow: "rgb(31 70 111 / 0.10)", artPosition: "58% 42%" },
  editorial: { accent: "#b84631", glow: "rgb(184 70 49 / 0.10)", artPosition: "58% 42%" },
  research: { accent: "#28625a", glow: "rgb(40 98 90 / 0.10)", artPosition: "52% 42%" },
  analytics: { accent: "#355b8d", glow: "rgb(53 91 141 / 0.11)", artPosition: "50% 45%" },
  setup: { accent: "#7a5a21", glow: "rgb(122 90 33 / 0.10)", artPosition: "55% 43%" },
  admin: { accent: "#4b5563", glow: "rgb(75 85 99 / 0.10)", artPosition: "50% 45%" },
  security: { accent: "#254f71", glow: "rgb(37 79 113 / 0.10)", artPosition: "50% 45%" },
  empty: { accent: "#68716f", glow: "rgb(104 113 111 / 0.09)", artPosition: "58% 45%" },
};

function inferMood(art?: PhotoName): PageMood {
  if (!art) return "daily";
  if (["articles", "article-detail", "notes", "review", "renewal", "wordpress", "feature-outline-board", "feature-supervision", "feature-publishing", "feature-knowledge-notes"].includes(art)) return "editorial";
  if (["keywords", "calendar", "feature-keywords-discovery", "feature-tax-calendar"].includes(art)) return "research";
  if (["dashboard-desk", "report", "usage", "admin-usage", "feature-growth-review", "feature-monthly-report"].includes(art)) return "analytics";
  if (["settings", "setup", "supervisors", "feature-secure-onboarding"].includes(art)) return "setup";
  if (["feature-fact-check"].includes(art)) return "security";
  if (["admin-offices", "admin-accounts"].includes(art)) return "admin";
  if (["account", "login-secure"].includes(art)) return "security";
  if (["empty-home", "not-found"].includes(art)) return "empty";
  return "daily";
}

/**
 * A signed-in screen: the page's photograph as a full-width header (navy
 * shade, breadcrumbs, a white Mincho title, the actions), then the page's
 * sections, each on a full-width band in the landing page's colours (warm
 * paper, blue-gray, white, sage, in turn). The bands and the header reach
 * the edges of the work area (`.workspace-column` is the size container).
 */
export function Page({ title, crumbs = [], actions, lead, art, mood, children }: { title: ReactNode; crumbs?: Crumb[]; actions?: ReactNode; lead?: ReactNode; art?: PhotoName; mood?: PageMood; children: ReactNode }) {
  useBackdrop(art);
  const currentMood = MOODS[mood ?? inferMood(art)];
  const style = { "--page-accent": currentMood.accent, "--page-glow": currentMood.glow } as CSSProperties;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }} className="workspace-page relative isolate" style={style}>
      <header className="page-hero">
        {art && (
          <motion.div aria-hidden className="absolute inset-0" initial={{ scale: 1.06 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}>
            <Photo name={art} fill priority sizes="100vw" position={currentMood.artPosition} decorative />
          </motion.div>
        )}
        <div aria-hidden className="page-hero-shade" />
        <div className="page-hero-inner">
          <nav aria-label="パンくずリスト" className="mb-4 flex flex-wrap items-center gap-1 text-xs text-white/70">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-white hover:underline">
              <Home className="size-3.5" />
              ホーム
            </Link>
            {crumbs.map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1">
                <ChevronRight className="size-3.5 opacity-60" />
                {c.to ? (
                  <Link to={c.to} className="hover:text-white hover:underline">
                    {c.label}
                  </Link>
                ) : (
                  <span className="text-white/90">{c.label}</span>
                )}
              </span>
            ))}
          </nav>
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
            <motion.div className="min-w-0" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
              <span aria-hidden className="page-hero-mark" />
              <h1 className="font-display text-[1.7rem] leading-snug font-semibold text-white sm:text-[2.3rem]">{title}</h1>
              {lead && <div className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-200 sm:text-[15px]">{lead}</div>}
            </motion.div>
            {actions && <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">{actions}</div>}
          </div>
        </div>
      </header>
      <div className="page-sections">{children}</div>
    </motion.div>
  );
}
